import { useContext, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Feeds1, Feeds2, Feeds3 } from '@hungryroot/icons';
import { Heading } from '@hungryroot/ramen';
import { ControlRadio } from '../../components/ControlRadio/ControlRadio';
import { ControlRange } from '../../components/ControlRange/ControlRange';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import {
  type QuizToastObj,
  QuizToastVariant,
} from '../../components/QuizToast/quiz-toast';
import { QuizToast } from '../../components/QuizToast/QuizToast/QuizToast';
import { Rule, RuleEffect } from '../../components/Rule/Rule';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { ToggleVariant, Toggle } from '../../components/Toggle/Toggle/Toggle';
import { HouseholdSize } from '../../defs/quiz';
import {
  ADULTS_COUNT_PROP,
  CHILDREN_COUNT_PROP,
  YOUNG_CHILDREN_COUNT_PROP,
} from '../../questionProps';
import { QuizContext } from '../../quiz-context';

import { hasMetQuizHelloRequirements } from '../../utils/hasMetQuizHelloRequirements';
import helloStyles from './Hello.module.css';
import styles from '../layout.module.css';

interface Props {
  testid: string;
}

const MIN_CONTROL_RANGE_VALUE = 0;
const MAX_CONTROL_RANGE_VALUE = 10;

export const AGE_CONTROL_LABELS = {
  ADULTS: 'Adults',
  CHILDREN: 'Children, ages 9 and over',
  YOUNG_CHILDREN: 'Children, ages 8 and under',
};

export function Hello(props: Props) {
  const { testid } = props;
  const { quiz, quizToast, onQuestionAnswer } = useContext(QuizContext);

  const [hasSeenChildrenToast, setHasSeenChildrenToast] = useState(false);

  const { householdSize, adultsCount, childrenCount, youngChildrenCount } =
    quiz;
  const householdRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!quizToast) {
      return;
    }

    const { variant } = quizToast;

    if (variant === QuizToastVariant.Children && !hasSeenChildrenToast) {
      renderToast(quizToast);
      setHasSeenChildrenToast(true);
    }
  }, [quizToast]);

  const renderToast = (quizToast: QuizToastObj) => {
    const {
      content: { text, icon },
      toast,
    } = quizToast;

    return toast(
      <QuizToast testid={`${testid}-toast`} icon={icon}>
        {text}
      </QuizToast>
    );
  };

  const toggleVariant = ToggleVariant.One;

  const defaultAdultsAccountValue = 1;

  const showFamilyCountQuestion =
    householdSize === HouseholdSize.ME_PLUS_ONE ||
    householdSize === HouseholdSize.ME_PLUS_MORE;

  const nextButtonDisabled = !hasMetQuizHelloRequirements(quiz);

  return (
    <QuizQuestionScreen
      submitButtonDisabled={nextButtonDisabled}
      testid={testid}
      heroText="Who are you feeding?"
      sectionLabel={
        <span className={helloStyles['section-label']}>
          <SectionLabel
            className={helloStyles['section-label-text']}
            label="About you"
          />
        </span>
      }
    >
      <ControlRadio question="householdSize" layoutVariant="two-column">
        <ControlRadio.Option value={HouseholdSize.ME}>
          <Toggle
            testid={`${testid}-toggle-household-size-me`}
            active={householdSize === HouseholdSize.ME}
            label="Me"
            variant={toggleVariant}
            illustration={<Feeds1 />}
            className={styles['animation-delay-point-5s']}
          />
        </ControlRadio.Option>
        <ControlRadio.Option value={HouseholdSize.ME_PLUS_ONE}>
          <Toggle
            testid={`${testid}-toggle-household-size-me-plus-one`}
            active={householdSize === HouseholdSize.ME_PLUS_ONE}
            label="Me + 1"
            variant={toggleVariant}
            illustration={<Feeds2 />}
            className={styles['animation-delay-point-55s']}
          />
        </ControlRadio.Option>
        <ControlRadio.Option value={HouseholdSize.ME_PLUS_MORE}>
          <Toggle
            testid={`${testid}-toggle-household-size-me-plus-more`}
            active={householdSize === HouseholdSize.ME_PLUS_MORE}
            label="Me + more"
            variant={toggleVariant}
            illustration={<Feeds3 />}
            className={styles['animation-delay-point-6s']}
          />
        </ControlRadio.Option>
      </ControlRadio>
      <Rule active={showFamilyCountQuestion} effect={RuleEffect.Show}>
        <section
          className={clsx({
            [styles['control-wrapper-match-grid-width']]: true,
          })}
          ref={householdRef}
        >
          <div
            className={`${styles['mid-page-question']} ${styles['slide-in']}`}
          >
            <Heading variant="H3">
              Select how many people are in your household.
            </Heading>
          </div>
          <ControlRange
            testid={`${testid}-question-number-adults`}
            label={AGE_CONTROL_LABELS.ADULTS}
            count={
              adultsCount === null ? defaultAdultsAccountValue : adultsCount
            }
            onChange={(value) => {
              onQuestionAnswer(quiz, ADULTS_COUNT_PROP, value);
            }}
            min={MIN_CONTROL_RANGE_VALUE}
            max={MAX_CONTROL_RANGE_VALUE}
          />
          <ControlRange
            testid={`${testid}-question-number-children`}
            label={AGE_CONTROL_LABELS.CHILDREN}
            count={childrenCount}
            onChange={(value) => {
              onQuestionAnswer(quiz, CHILDREN_COUNT_PROP, value);
            }}
            min={MIN_CONTROL_RANGE_VALUE}
            max={MAX_CONTROL_RANGE_VALUE}
          />
          <ControlRange
            testid={`${testid}-question-number-young-children`}
            label={AGE_CONTROL_LABELS.YOUNG_CHILDREN}
            count={youngChildrenCount}
            onChange={(value) =>
              onQuestionAnswer(quiz, YOUNG_CHILDREN_COUNT_PROP, value)
            }
            min={MIN_CONTROL_RANGE_VALUE}
            max={MAX_CONTROL_RANGE_VALUE}
          />
        </section>
      </Rule>
    </QuizQuestionScreen>
  );
}
